import { AnalyticsUserEvent } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledInput,
    FrostedSurface,
    isNonMobileWeb,
    Spinner,
    View,
} from 'smoothie';
import z from 'zod';

import { NewMemberAccountDisclosure } from '../../../banking/components/Disclosures';
import { isEmail } from '../../../common/misc';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import {
    captureMessage,
    track,
    trackGrowthMarketingEvent,
} from '../../../logger/Logger';
import { useUpsertVisitor } from '../../../user/hooks/useUpsertVisitor';
import { useUser } from '../../../user/providers/UserProvider';
import { STYTCH_ERROR_MESSAGE_BY_TYPE } from '../../constants';
import { useCreateStytchAccount } from '../../hooks/start-membership/useCreateStytchAccount';
import { useReturningMemberPasswordReset } from '../../hooks/start-membership/useReturningMemberPasswordReset';
import { useStytchSession, useStytchUser } from '../../hooks/useAuth';
import { isUserSetupInProgressAtom } from '../../state/AuthAtoms';
import { PasswordStrengthChecker } from '../PasswordStrengthChecker';
import ShowPasswordToggle from '../ShowPasswordToggle';
import { NewMemberScreenLayout } from './NewMemberScreenLayout';
import {
    ActiveAccountSignInMessage,
    ReturningMemberCheckEmailMessage,
    ReturningMemberErrorMessage,
} from './ReturningMemberMessages';

const createAccountSchema = z.object({
    email: z.string().refine(isEmail, 'Please enter a valid email address.'),
    password: z.string().min(1, 'Please enter a password.'),
});
type CreateAccountSchema = z.infer<typeof createAccountSchema>;

export const NewMemberAccount = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('SignUp/NewMemberAccount');

    const { user } = useUser();
    const [showPassword, setShowPassword] = useState(false);
    const [authError, setAuthError] = useState<Error | null>(null);
    const [isPasswordValid, setIsPasswordValid] = useState(true);
    const {
        hasTriggeredReturningMemberPasswordReset,
        hasMatchedActiveAccount,
        hasEncounteredError,
        handleDuplicateEmail,
    } = useReturningMemberPasswordReset();
    const setIsUserSetupInProgress = useSetAtom(isUserSetupInProgressAtom);
    const searchParams = useLocalSearchParams();
    const { user: stytchUser } = useStytchUser();
    const { session: stytchSession } = useStytchSession();
    const upsertVisitor = useUpsertVisitor();

    const form = useForm<CreateAccountSchema>({
        resolver: zodResolver(createAccountSchema),
        defaultValues: { email: '', password: '' },
    });
    const watchedEmail = form.watch('email');
    const watchedPassword = form.watch('password');

    const { createStytchAccount, isCreatingStytchAccount } =
        useCreateStytchAccount();

    const onPasswordStrengthChange = useCallback(() => {
        setAuthError(null);
    }, []);

    const onCreateStytchAccountSuccess = useCallback(
        (email: string) => {
            upsertVisitor();
            track(AnalyticsUserEvent.SIGNUP_FUNNEL_NEW_MEMBER_ACCOUNT_CREATED);
            trackGrowthMarketingEvent(
                AnalyticsUserEvent.SIGNUP_FUNNEL_SURVEY_EMAIL_SUBMIT,
                { email },
            );
            setIsUserSetupInProgress(true);
            // The next screen reads the newly-created identity straight from the
            // Stytch session hooks rather than via a forwarded atom.
            router.push({
                pathname: '/sign-up/new-member/info',
                params: searchParams,
            });
        },
        [searchParams, setIsUserSetupInProgress, upsertVisitor],
    );

    const onCreateStytchAccountError = useCallback(
        async (error: Error) => {
            // Returning/active member — trigger a reset email instead of
            // showing "email already in use". Backend decides which case it is.
            if (
                error.message === STYTCH_ERROR_MESSAGE_BY_TYPE.duplicate_email
            ) {
                await handleDuplicateEmail(form.getValues('email'));
                return;
            }

            form.setError('root', {
                type: 'manual',
                message: error.message,
            });
            setAuthError(error);
        },
        [form, handleDuplicateEmail],
    );

    const onFormSubmit = useCallback(
        (formData: CreateAccountSchema) => {
            setAuthError(null);

            // Skip ahead if a Stytch session already exists in this browser
            // (e.g. a partially-completed signup) rather than trying to
            // create a second password identity on top of it.
            if (stytchUser && stytchSession) {
                captureMessage(
                    'Authentication account creation skipped -- session already exists',
                );
                onCreateStytchAccountSuccess(formData.email);
                return;
            }

            void createStytchAccount({
                email: formData.email,
                password: formData.password,
                onSuccess: () => {
                    onCreateStytchAccountSuccess(formData.email);
                },
                onError: onCreateStytchAccountError,
            });
        },
        [
            createStytchAccount,
            onCreateStytchAccountSuccess,
            onCreateStytchAccountError,
            stytchUser,
            stytchSession,
        ],
    );

    const hasAuthError =
        authError?.message != null && authError.message.length > 0;

    const isSubmitDisabled =
        isCreatingStytchAccount ||
        !isEmail(watchedEmail) ||
        !isPasswordValid ||
        hasAuthError;

    if (user?.isFullyAuthed) {
        return <Redirect href="/home" />;
    }

    if (hasEncounteredError) {
        return (
            <NewMemberScreenLayout title="Something went wrong">
                <FrostedSurface padding={24} borderRadius={20}>
                    <ReturningMemberErrorMessage />
                </FrostedSurface>
            </NewMemberScreenLayout>
        );
    }

    if (hasMatchedActiveAccount) {
        return (
            <NewMemberScreenLayout title="Welcome back">
                <FrostedSurface padding={24} borderRadius={20}>
                    <ActiveAccountSignInMessage />
                </FrostedSurface>
            </NewMemberScreenLayout>
        );
    }

    if (hasTriggeredReturningMemberPasswordReset) {
        return (
            <NewMemberScreenLayout title="Check your email">
                <FrostedSurface padding={24} borderRadius={20}>
                    <ReturningMemberCheckEmailMessage />
                </FrostedSurface>
            </NewMemberScreenLayout>
        );
    }

    return (
        <NewMemberScreenLayout title="Save your Money Map">
            <FrostedSurface padding={24} borderRadius={20}>
                <ControlledInput
                    name="email"
                    label="Email address"
                    placeholder="Enter email address"
                    autoComplete="email"
                    autoCapitalize="none"
                    inputMode="email"
                    keyboardType="email-address"
                    textContentType="username"
                    control={form.control}
                    register={form.register}
                    errors={form.formState.errors}
                    onSubmitEditing={() => {
                        form.setFocus('password');
                    }}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={isNonMobileWeb}
                    disabled={isCreatingStytchAccount}
                />

                <View marginTop={16}>
                    <ControlledInput
                        name="password"
                        label="Password"
                        placeholder="Create a password"
                        autoCapitalize="none"
                        autoComplete="new-password"
                        inputMode="text"
                        keyboardType="default"
                        textContentType="password"
                        secureTextEntry={!showPassword}
                        control={form.control}
                        register={form.register}
                        errors={form.formState.errors}
                        onSubmitEditing={form.handleSubmit(onFormSubmit)}
                        rightSideContent={
                            <ShowPasswordToggle
                                showPassword={showPassword}
                                setShowPassword={setShowPassword}
                            />
                        }
                        disabled={isCreatingStytchAccount}
                    />
                    <PasswordStrengthChecker
                        password={watchedPassword}
                        onValidationChange={setIsPasswordValid}
                        onStrengthCheckChange={onPasswordStrengthChange}
                    />
                </View>

                <View
                    animation="quick"
                    opacity={hasAuthError ? 1 : 0}
                    height={hasAuthError ? 48 : 0}
                    marginTop={hasAuthError ? 16 : 0}
                    overflow="hidden"
                    animateOnly={['opacity', 'height', 'marginTop']}
                >
                    <BodySmallRegular color="$red1100">
                        {authError?.message ?? ''}
                    </BodySmallRegular>
                </View>

                <View marginTop={16}>
                    <NewMemberAccountDisclosure />
                </View>

                <View marginTop={16}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        onPress={form.handleSubmit(onFormSubmit)}
                        disabled={isSubmitDisabled}
                        text={isCreatingStytchAccount ? '' : 'Create account'}
                        icon={isCreatingStytchAccount ? <Spinner /> : null}
                    />
                </View>
            </FrostedSurface>
        </NewMemberScreenLayout>
    );
};
